/* ==========================================================================
   CART PANEL
   Overlay that drops from the header bag on desktop and tablet. Phones follow
   the link to /cart instead — a hovering panel is worse than a page there.

   Contents come from /cart.js, so the panel is always in step with the real
   cart, and quantity changes go through /cart/change.js.
   ========================================================================== */

(function () {
  'use strict';

  const PANEL_MIN_WIDTH = 700;
  // A hover-capable, fine pointer means a desktop mouse. Touch tablets report
  // (hover: none), so they keep tap-to-open rather than opening on a stray
  // touch, and phones never open the panel at all.
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const canPanel = () => window.matchMedia('(min-width: ' + PANEL_MIN_WIDTH + 'px)').matches;
  const panelEl = () => document.querySelector('[data-syn-cart-panel]');
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const money = (cents) => {
    const fmt = window.themeVariables?.settings?.moneyFormat;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && fmt) {
      return window.Shopify.formatMoney(cents, fmt);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* Product titles carry the brand as a prefix so Shopify can sort collections
     by brand -- its storefront has no vendor sort. The brand already has its
     own line here, so drop the duplicate. Mirrors snippets/synergy-product-title.liquid. */
  const titleWithoutBrand = (title, vendor) => {
    if (!title || !vendor) return title || '';
    return title.slice(0, vendor.length) === vendor
      ? title.slice(vendor.length).trim() || title
      : title;
  };

  /* ---------- Open / close ---------- */

  /* ---------- Hover (desktop only) ---------- */
  const openerEl = () => document.querySelector('[data-syn-cart-open]');
  const openerItem = () => openerEl()?.closest('li');

  let hoverTimer = null;
  const cancelClose = () => { window.clearTimeout(hoverTimer); };
  const scheduleClose = () => {
    cancelClose();
    // Long enough to move the pointer from the icon down onto the panel.
    hoverTimer = window.setTimeout(closePanel, 260);
  };

  function bindHover() {
    const item = openerItem();
    if (!item) return;
    item.addEventListener('mouseenter', () => { if (canHover()) { cancelClose(); openPanel(); } });
    item.addEventListener('mouseleave', () => { if (canHover()) scheduleClose(); });

    const panel = panelEl();
    if (panel) {
      panel.addEventListener('mouseenter', cancelClose);
      panel.addEventListener('mouseleave', () => { if (canHover()) scheduleClose(); });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindHover);
  else bindHover();


  /* Put the panel just under its header icon.

     "position: fixed" is not always relative to the viewport: the sticky header
     establishes a containing block, so a panel inside it is positioned against
     the header instead, and every panel landed ~60px too low. Rather than
     hard-code that offset -- it changes with the header -- place the panel, see
     where it actually landed, and correct by the difference.

     The measurement neutralises the entry transform first -- and its transition,
     or the read lands mid-animation and the 6px the panel rises through gets
     corrected away as if it were drift. */
  const PANEL_GAP = 10;

  function placePanel(panel) {
    const opener = document.querySelector('[data-syn-cart-open]');
    if (!panel || !opener) return;

    // The icon itself, not its link box, is what the panel should hang from.
    const rect = (opener.querySelector('svg') || opener).getBoundingClientRect();
    const wantTop = Math.round(rect.bottom + PANEL_GAP);
    const wantEnd = Math.max(12, Math.round(window.innerWidth - rect.right - 8));

    panel.style.top = wantTop + 'px';
    panel.style.insetInlineEnd = wantEnd + 'px';

    const transform = panel.style.transform;
    const transition = panel.style.transition;
    panel.style.transition = 'none';
    panel.style.transform = 'none';
    const got = panel.getBoundingClientRect();
    panel.style.transform = transform;
    panel.style.transition = transition;

    const driftY = Math.round(got.top - wantTop);
    const driftX = Math.round(window.innerWidth - got.right - wantEnd);
    if (driftY) panel.style.top = wantTop - driftY + 'px';
    if (driftX) panel.style.insetInlineEnd = wantEnd - driftX + 'px';
  }


  function openPanel(cart) {
    const panel = panelEl();
    if (!panel || !canPanel()) return false;
    if (!panel.hidden) return true;          // already open; hover re-fires a lot
    panel.hidden = false;
    placePanel(panel);
    openerItem()?.classList.add('is-panel-open');
    requestAnimationFrame(() => panel.classList.add('is-open'));
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onOutside, true);
    render(cart);
    return true;
  }

  function closePanel() {
    const panel = panelEl();
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    openerItem()?.classList.remove('is-panel-open');
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('click', onOutside, true);
    const done = () => { panel.hidden = true; };
    panel.addEventListener('transitionend', done, { once: true });
    window.setTimeout(done, 300);
  }

  const onKey = (e) => { if (e.key === 'Escape') closePanel(); };

  function onOutside(event) {
    const panel = panelEl();
    if (!panel || panel.hidden) return;
    if (panel.contains(event.target) || event.target.closest('[data-syn-cart-open]')) return;
    closePanel();
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-syn-cart-open]');
    if (opener) {
      // Desktop: hover already shows the panel, so a click follows the link to
      // the full page. Touch tablet: no hover, so the tap toggles the panel.
      // Phone: no panel, link followed.
      if (canPanel() && !canHover()) {
        event.preventDefault();
        const panel = panelEl();
        if (panel && !panel.hidden) closePanel();
        else openPanel();
      }
      return;
    }
    if (event.target.closest('[data-syn-cart-close]')) closePanel();
  });

  const reposition = () => {
    const panel = panelEl();
    if (panel && !panel.hidden) placePanel(panel);
  };
  window.addEventListener('resize', () => { if (!canPanel()) closePanel(); else reposition(); }, { passive: true });
  window.addEventListener('scroll', reposition, { passive: true });

  /* ---------- Rendering ---------- */
  /* An add opened the panel and then re-fetched /cart.js, so for a moment it
     could show the bag as it was before the add -- and with two renders in
     flight the older response could land last and stick. Paint from the cart
     the add already carries when there is one, never from a cached response,
     and let only the newest render write to the panel. */
  let renderSeq = 0;

  async function render(given) {
    const panel = panelEl();
    if (!panel) return;
    const body = panel.querySelector('[data-syn-cart-body]');
    const foot = panel.querySelector('[data-syn-cart-foot]');
    if (!body) return;

    const seq = ++renderSeq;
    let cart = given && Array.isArray(given.items) ? given : null;
    if (!cart) {
      try {
        cart = await (await fetch(root() + 'cart.js', { cache: 'no-store' })).json();
      } catch (e) {
        return;
      }
    }
    if (seq !== renderSeq) return;

    paintCount(cart.item_count);
    panel.querySelectorAll('[data-syn-cart-count-label]').forEach((el) => {
      el.textContent = cart.item_count === 1 ? '1 item' : cart.item_count + ' items';
    });

    if (!cart.items.length) {
      body.innerHTML = '<p class="syn-cart__empty">Your bag is empty.</p>';
      if (foot) foot.hidden = true;
      return;
    }

    if (foot) {
      foot.hidden = false;
      const total = foot.querySelector('[data-syn-cart-total]');
      if (total) total.textContent = money(cart.total_price);
    }

    body.innerHTML =
      '<div class="syn-cart__list">' + cart.items.map(lineHtml).join('') + '</div>';
    enrichSalePrices(cart);
    matchScrollbarInset();
  }

  /* The list scrolls, so a scrollbar takes width out of it; the footer does
     not, and its grand total ended up sitting further right than the per-line
     totals above. Publish the scrollbar width so the total row can match. */
  function matchScrollbarInset() {
    const panel = document.querySelector('[data-syn-cart-panel]');
    const body = panel && panel.querySelector('.syn-panel__body');
    if (!panel || !body) return;
    const width = Math.max(0, body.offsetWidth - body.clientWidth);
    panel.style.setProperty('--syn-panel-scrollbar', width + 'px');
  }

  /* /cart.js does not carry compare_at_price, so a sale cannot be detected from
     the cart alone. Each distinct product is fetched once -- small, cached
     responses -- and any line whose variant has a higher compare-at gets the
     struck-through figure and the red price. If a fetch fails the line simply
     stays at full price, which is the safe direction to be wrong in. */
  async function enrichSalePrices(cart) {
    const blocks = [...document.querySelectorAll('[data-syn-cart-panel] .syn-cart__figures')];
    if (!blocks.length) return;
    const handles = [...new Set(blocks.map((b) => b.dataset.handle).filter(Boolean))];

    await Promise.all(handles.map(async (handle) => {
      let product;
      try {
        const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
        if (!res.ok) return;
        product = await res.json();
      } catch (e) { return; }

      const byId = new Map((product.variants || []).map((v) => [String(v.id), v]));
      for (const block of blocks) {
        if (block.dataset.handle !== handle) continue;
        const variant = byId.get(String(block.dataset.variant));
        if (!variant || !variant.compare_at_price) continue;
        if (variant.compare_at_price <= variant.price) continue;

        const compare = block.querySelector('[data-syn-compare]');
        const price = block.querySelector('[data-syn-unit-price]');
        if (compare) { compare.textContent = money(variant.compare_at_price); compare.hidden = false; }
        if (price) price.classList.add('syn-cart__figure-value--sale');
      }
    }));
  }

  function lineHtml(item, index) {
    const img = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '';
    // Only real options are worth showing; a single-variant product reports
    // "Default Title", which tells the shopper nothing.
    const options = (item.options_with_values || [])
      .filter((o) => o.value && !/default title/i.test(o.value))
      .map((o) => escapeHtml(o.name) + ': ' + escapeHtml(o.value))
      .join(' · ');

    return (
      '<div class="syn-cart__item" data-line="' + (index + 1) + '" data-handle="' + escapeHtml(item.handle) + '">' +
        '<a class="syn-cart__media" href="' + item.url + '">' +
          (img ? '<img src="' + img + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy">' : '') +
        '</a>' +
        '<div class="syn-cart__info">' +
          (item.vendor ? '<span class="syn-cart__brand">' + escapeHtml(item.vendor) + '</span>' : '') +
          '<a class="syn-cart__title" href="' + item.url + '">' + escapeHtml(titleWithoutBrand(item.product_title, item.vendor)) + '</a>' +
          (options ? '<span class="syn-cart__options">' + options + '</span>' : '') +
          // Concentration on its own line -- EDT, EDP and so on -- which the
          // page shows but the panel was dropping.
          (item.product_type ? '<span class="syn-cart__type">' + escapeHtml(item.product_type) + '</span>' : '') +
          '<div class="syn-cart__actions">' +
            '<button type="button" class="syn-cart__action" data-syn-cart-wish>' +
              '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.25C7.5 16.5 3.75 13.2 3.75 9.375A4.125 4.125 0 0 1 12 7.125a4.125 4.125 0 0 1 8.25 2.25c0 3.825-3.75 7.125-8.25 10.875Z"/></svg>' +
              'Move to wish list</button>' +
            '<button type="button" class="syn-cart__action" data-syn-cart-remove>' +
              '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V5h6v2M7 7l1 13h8l1-13"/></svg>' +
              'Remove</button>' +
          '</div>' +
          '<div class="syn-cart__row">' +
            '<div class="syn-cart__qty">' +
              '<button type="button" data-syn-cart-step="-1" aria-label="Decrease quantity">&minus;</button>' +
              '<span>' + item.quantity + '</span>' +
              '<button type="button" data-syn-cart-step="1" aria-label="Increase quantity">+</button>' +
            '</div>' +
            /* Unit price and line total, labelled, as on the cart page. The
               struck-through figure is filled in afterwards -- see enrichSalePrices. */
            '<div class="syn-cart__figures" data-variant="' + item.variant_id + '" data-handle="' + escapeHtml(item.handle) + '">' +
              '<span class="syn-cart__figure">' +
                '<span class="syn-cart__figure-label">Price</span>' +
                '<span class="syn-cart__figure-row">' +
                  '<span class="syn-cart__figure-value" data-syn-unit-price>' + money(item.original_price) + '</span>' +
                  '<s class="syn-cart__compare" data-syn-compare hidden></s>' +
                '</span>' +
              '</span>' +
              '<span class="syn-cart__figure">' +
                '<span class="syn-cart__figure-label">Total</span>' +
                '<span class="syn-cart__figure-value">' + money(item.original_line_price) + '</span>' +
              '</span>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  /* ---------- Line changes ---------- */
  async function changeLine(line, quantity) {
    try {
      const res = await fetch(root() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ line: line, quantity: quantity }),
      });
      if (!res.ok) return;
      const cart = await res.json();
      // Tell the theme, so any cart count or cart page elsewhere updates too.
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true, detail: { baseEvent: 'line-item:change', cart: cart },
      }));
      render(cart);
    } catch (e) { /* leave the panel as it is */ }
  }

  document.addEventListener('click', (event) => {
    const item = event.target.closest('.syn-cart__item');
    if (!item) return;
    const line = Number(item.getAttribute('data-line'));

    const step = event.target.closest('[data-syn-cart-step]');
    if (step) {
      const current = Number(item.querySelector('.syn-cart__qty span').textContent) || 1;
      changeLine(line, Math.max(0, current + Number(step.getAttribute('data-syn-cart-step'))));
      return;
    }

    if (event.target.closest('[data-syn-cart-remove]')) {
      changeLine(line, 0);
      return;
    }

    if (event.target.closest('[data-syn-cart-wish]')) {
      const handle = item.getAttribute('data-handle');
      if (window.SynergyWishlist && handle && !window.SynergyWishlist.has(handle)) {
        window.SynergyWishlist.toggle(handle);
      }
      changeLine(line, 0);
    }
  });

  /* ---------- Header count ---------- */
  function paintCount(n) {
    document.querySelectorAll('[data-syn-cart-count]').forEach((el) => {
      el.textContent = String(n);
      el.hidden = !n;
    });
    /* Prestige renders its own <cart-count> inside the bag link's screen-reader
       label. Left unpainted it drifts out of step with the visible badge. */
    document.querySelectorAll('cart-count').forEach((el) => { el.textContent = String(n); });
  }

  /* ---------- Phone: "Added to your bag" bar ----------

     Phones never open the panel (see canPanel), and until this existed nothing
     replaced it: on a phone, Add to Bag changed a one-pixel badge in the corner
     and nothing else. No confirmation, no Checkout. Meta traffic is almost all
     phones, and on 2026-09-10/11 Shopify counted 238 social sessions, 18 with a
     cart addition and ZERO that reached checkout -- the shoppers were willing,
     the page gave them nowhere to go.

     So below the panel breakpoint an add slides up a bar with the two things a
     shopper wants next. Styles live here rather than in a stylesheet because
     this script loads on every page and the bar must never render unstyled on
     a page whose CSS happens not to include it. */
  const BAR_STYLE = `
    .syn-addedbar{position:fixed;left:0;right:0;bottom:0;z-index:2147483000;
      background:#fff;border-top:1px solid #e5e5e5;box-shadow:0 -6px 24px rgba(0,0,0,.12);
      padding:14px 16px calc(14px + env(safe-area-inset-bottom));
      font-family:var(--text-font-family,inherit);color:#111;
      transform:translateY(110%);transition:transform .25s ease}
    .syn-addedbar.is-open{transform:translateY(0)}
    .syn-addedbar__head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:0 0 12px}
    .syn-addedbar__msg{margin:0;font-size:15px;font-weight:600;line-height:1.3}
    .syn-addedbar__close{background:none;border:0;padding:4px;margin:-4px;cursor:pointer;color:#111;line-height:0}
    .syn-addedbar__close svg{width:20px;height:20px}
    .syn-addedbar__actions{display:grid;grid-template-columns:1fr 1fr;gap:10px}
    .syn-addedbar__btn{display:block;padding:13px 12px;text-align:center;text-decoration:none;
      font-size:14px;font-weight:600;letter-spacing:.04em;text-transform:none;
      border:1px solid #111;transition:background-color .15s ease,color .15s ease}
    .syn-addedbar__btn--ghost{background:#fff;color:#111}
    .syn-addedbar__btn--solid{background:#111;color:#fff}
    .syn-addedbar__btn--ghost:hover,.syn-addedbar__btn--ghost:focus-visible{background:#111;color:#fff}
    .syn-addedbar__btn--solid:hover,.syn-addedbar__btn--solid:focus-visible{background:#fff;color:#111}`;

  let addedBar = null;
  let addedBarTimer = null;

  function ensureAddedBar() {
    if (addedBar) return addedBar;
    const style = document.createElement('style');
    style.textContent = BAR_STYLE;
    document.head.appendChild(style);

    addedBar = document.createElement('div');
    addedBar.className = 'syn-addedbar';
    addedBar.setAttribute('role', 'dialog');
    addedBar.setAttribute('aria-label', 'Added to your bag');
    addedBar.innerHTML =
      '<div class="syn-addedbar__head">' +
        '<p class="syn-addedbar__msg" role="status" aria-live="polite" data-syn-addedbar-msg>Added to your bag</p>' +
        '<button type="button" class="syn-addedbar__close" aria-label="Close" data-syn-addedbar-close>' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="syn-addedbar__actions">' +
        '<a class="syn-addedbar__btn syn-addedbar__btn--ghost" href="' + root() + 'cart">View bag</a>' +
        '<a class="syn-addedbar__btn syn-addedbar__btn--solid" href="' + root() + 'cart/checkout">Checkout</a>' +
      '</div>';
    document.body.appendChild(addedBar);
    addedBar.querySelector('[data-syn-addedbar-close]').addEventListener('click', hideAddedBar);
    return addedBar;
  }

  function showAddedBar(cart) {
    const bar = ensureAddedBar();
    const msg = bar.querySelector('[data-syn-addedbar-msg]');
    const n = cart && cart.item_count;
    if (msg) msg.textContent = n > 1 ? 'Added to your bag · ' + n + ' items' : 'Added to your bag';
    bar.hidden = false;
    requestAnimationFrame(() => bar.classList.add('is-open'));
    /* Long enough to read and reach for; it slides away rather than sitting
       over the page for good. Checkout is always a tap on the bag icon after. */
    window.clearTimeout(addedBarTimer);
    addedBarTimer = window.setTimeout(hideAddedBar, 12000);
  }

  function hideAddedBar() {
    if (!addedBar) return;
    window.clearTimeout(addedBarTimer);
    addedBar.classList.remove('is-open');
  }

  // The theme fires this after any add or change, including from a product page.
  document.documentElement.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart) paintCount(cart.item_count);
    const panel = panelEl();
    if (panel && !panel.hidden) render(cart);
    // An add-to-cart should show what just happened, as the drawer used to:
    // the panel where there is room for one, the bar on a phone.
    if (event.detail && event.detail.baseEvent === 'variant:add') {
      if (canPanel()) openPanel(cart);
      else showAddedBar(cart);
    }
  });

  /* The cart PAGE updates quantities itself and announces the result on
     `document` as synergy:cart:updated. Nothing was listening, so changing a
     quantity there left the header badge showing the old count until the next
     page load -- most visible on mobile, where the badge sits right above the
     line being edited. Prestige's own <cart-count> element is painted too, so
     the two cannot disagree. */
  document.addEventListener('synergy:cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart) paintCount(cart.item_count);
  });

  document.addEventListener('DOMContentLoaded', () => {
    const el = document.querySelector('[data-syn-cart-count]');
    if (el) paintCount(Number(el.getAttribute('data-initial') || 0));
  });
})();
